import { useEffect, useId, useRef } from 'react';
import type { View } from '../route.js';
import { Icon } from './Board.js';
import type { MatchKind } from '../board.js';

const DEMO: { match: MatchKind; label: string; value: string; text: string }[] = [
  { match: 'correct', label: 'Region', value: 'Ionia', text: 'Exactly the same as the answer.' },
  {
    match: 'partial',
    label: 'Position',
    value: 'Top, Mid',
    text: 'Some values are shared, but not all. Only for attributes that can have several values.',
  },
  {
    match: 'incorrect',
    label: 'Species',
    value: 'Human',
    text: 'Nothing in common with the answer.',
  },
];

export function HowToPlay({ go }: { go: (view: View) => void }) {
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <section className="panel howto" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        How to play
      </h2>
      <p>
        A League of Legends champion is picked at random and hidden. Guess champions until you find
        it. Each guess shows how close it is on seven attributes.
      </p>
      <ol>
        <li>Type a champion&rsquo;s name and choose it from the list.</li>
        <li>Read the tiles, then use them to narrow down your next guess.</li>
        <li>Find the champion to win. You can give up at any time to see the answer.</li>
      </ol>

      <h3>What the tiles mean</h3>
      <ul className="tiles tiles--demo" aria-label="Tile examples">
        {DEMO.map((d) => (
          <li key={d.match} className={`tile tile--${d.match}`}>
            <span className="tile__inner">
              <span className="tile__label">{d.label}</span>
              <span className="tile__body">
                <Icon match={d.match} />
                <span className="tile__values">
                  <span>{d.value}</span>
                </span>
              </span>
            </span>
          </li>
        ))}
      </ul>
      <ul>
        {DEMO.map((d) => (
          <li key={d.match}>
            <strong>
              {d.match === 'correct' ? 'Check' : d.match === 'partial' ? 'Dash, striped' : 'Cross'}:
            </strong>{' '}
            {d.text}
          </li>
        ))}
      </ul>

      <h3>The seven attributes</h3>
      <ul>
        <li>
          <strong>Gender</strong> and <strong>Resource</strong> (mana, energy and so on): one value
          each, so a tile is either right or wrong.
        </li>
        <li>
          <strong>Position</strong>, <strong>Species</strong>, <strong>Range type</strong> and{' '}
          <strong>Region</strong>: a champion can have several. Sharing some but not all is a
          partial match.
        </li>
        <li>
          <strong>Release year</strong>: right or wrong, with an arrow. Up means the answer was
          released later than your guess, down means earlier.
        </li>
      </ul>

      <h3>Good to know</h3>
      <ul>
        <li>There is no limit on guesses and no daily limit: play as many games as you like.</li>
        <li>
          You never need an account. Create one if you want your history and stats to follow you
          across devices; the games you already played are kept.
        </li>
        <li>
          Stats show how your guesses compare with a perfect solver, so you can see whether you
          narrowed things down well or got lucky.
        </li>
        <li>Colours, theme and animation can be changed under Settings.</li>
      </ul>
      <div className="actions">
        <button type="button" className="primary" onClick={() => go('play')}>
          Start playing
        </button>
      </div>
    </section>
  );
}
